import { FormControl, FormGroup, Validators } from '@angular/forms';
import { createSelector } from 'reselect';
import { AppConfig } from '../../../app/app.config';
import {
    handleLoadNextPageAction,
    handleRehydrateAction,
    handleRemoveAction,
    handleSetFilterAction,
    handleSetTotalAction
} from '../../../core/store/helpers/reducer-helper';
import { PaginatableObjectFilters } from '../../store/bootstrap';
import { VasState } from '../../store/store-type';
import { Field } from '../field/field.model';
import * as fromFieldActions from './../field/field.actions';
import * as fromJobActions from './job.actions';
import { Job } from './job.model';
import { submitReducer } from './job.submit.reducer';
import { updateFromApiReducer } from './job.update-from-api.reducer';

export interface IJobFilters extends PaginatableObjectFilters {
    jobType?: string;
    jobStatus?: Array<string>;
    defaults?: boolean;
}

export interface State {
    ids: Array<string>;
    entities: {[id: string]: Job};
    total: number;
    filters: IJobFilters;
    ui: {
        loading: boolean
    };
}

export const initialState: State = {
    ids: [],
    entities: {},
    total: 0,
    filters: {
        searchTerm: '',
        jobType: '*',
        jobStatus: [],
        sortBy: AppConfig.JOB_LIST_PAGE.defaultSortBy,
        sortDirection: AppConfig.JOB_LIST_PAGE.defaultSortDirection,
        limit: AppConfig.JOB_LIST_PAGE.limit,
        offset: 0,
        // we use this value to indicate that the filters have their default values. We need this to decide whether to
        // to generate a list of job statuses to filter on when the user has logged in for the very first time. After
        // the user makes a change to the filters, this flag will be cleared (and persisted) after which we won't alter
        // the list of job statuses
        defaults: true
    },
    ui: {
        loading: false
    }
};

export const getFilterFormGroup = (
    searchTerm: string = '',
    jobType: string = '*',
    jobStatus: Array<string> = [],
    sortBy: string = 'jobStatus',
    sortDirection: number = 1,
    limit: number = AppConfig.JOB_LIST_PAGE.limit,
    offset: number = 0
): FormGroup => {
    return new FormGroup({
        searchTerm: new FormControl(searchTerm),
        jobType: new FormControl(jobType),
        jobStatus: new FormControl(jobStatus),
        sortBy: new FormControl(sortBy, Validators.required),
        sortDirection: new FormControl(sortDirection, Validators.required),
        limit: new FormControl(limit, Validators.required),
        offset: new FormControl(offset, Validators.required),
        // this FormControl is needed otherwise Angular will complain when we try to set the value of the FormGroup in
        // JobListFilterComponent
        defaults: new FormControl(false)
    });
};

// tslint:disable-next-line:only-arrow-functions cyclomatic-complexity
export function reducer(state = initialState, action: fromJobActions.Actions | fromFieldActions.Actions): State {

    switch (action.type) {
        case fromJobActions.SUBMIT: {
            return submitReducer(action, state);
        }
        case fromJobActions.REMOVE: {
            return handleRemoveAction(state, action);
        }
        case fromJobActions.SET_FILTER: {
            return handleSetFilterAction(state, new fromJobActions.SetFilterAction({
                ...action.payload,
                defaults: false
            }, action.options));
        }
        case fromJobActions.SET_TOTAL: {
            return handleSetTotalAction(state, action);
        }
        case fromJobActions.LOAD_NEXT_PAGE: {
            return handleLoadNextPageAction(state, action);
        }
        case fromJobActions.SET_UI_LOADING: {
            return {
                ...state,
                ui: {
                    ...state.ui,
                    loading: action.payload as boolean
                }
            };
        }
        case fromJobActions.BUMP_VERSION: {

            const job = state.entities[action.payload];

            if (!job) {
                return state;
            }

            return {
                ...state,
                entities: {
                    ...state.entities,
                    [job.id]: new Job({
                        ...job,
                        version: job.version + 1,
                        dirty: false
                    })
                }
            };
        }
        case fromFieldActions.BUMP_VERSION: {

            const job = state.entities[action.payload.jobId];

            if (!job || !job.fields.length) {
                return state;
            }

            let field = job.fields.find(f => f.id === action.payload.fieldId);

            if (!field) {
                return state;
            }

            field = new Field({
                ...field,
                version: field.version + 1,
                dirty: false
            });

            return {
                ...state,
                entities: {
                    ...state.entities,
                    [job.id]: new Job({
                        ...job,
                        fields: job.fields.map(f => {
                            return f.id === action.payload.fieldId ?
                                field :
                                f;
                        })
                    })
                }
            };
        }
        case fromJobActions.REHYDRATE: {
            return handleRehydrateAction(state, action);
        }
        case fromJobActions.RESET_FILTER_DEFAULTS_FLAG: {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    defaults: true
                }
            };
        }
        case fromJobActions.RESET_OFFSET: {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    offset: 0
                }
            };
        }
        case fromJobActions.UPDATE_FROM_API: {
            return updateFromApiReducer(action, state);
        }
        case fromJobActions.WIPE: {
            return {
                ...state,
                ids: [],
                entities: {}
            };
        }
        default: {
            return state;
        }
    }
}

export const getState = (state: VasState) => state.job;
export const getEntities = createSelector(
    getState,
    state => state.entities
);
export const getIds = createSelector(
    getState,
    state => state.ids
);
export const getAll = createSelector(
    getEntities,
    getIds,
    (entities, ids) =>
        ids.map(id => entities[id])
);



// WEBPACK FOOTER //
// ./src/vas/business-objects/job/job.reducer.ts